import { useState, useEffect, useRef } from 'react';
import { Settings, Save, Tag, Plus, Trash2, Check, Eye, EyeOff, Package, Euro, FileText, Upload, Image, Hash, TrendingUp, Wallet, Pencil, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';

export const SetariV2 = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const logoInputRef = useRef(null);
    const fullLogoInputRef = useRef(null);
    
    // Settings state
    const [settings, setSettings] = useState({
        allow_new_categories: true,
        default_category: '',
        categories: [],
        comenzi_palet_name: 'Comenzi din Palet',
        show_comenzi_palet: true,
        show_version_switch: true,
        allow_delete_pallets: false,  // Permite ștergerea paleților din istoric
        euro_rate: 5.0,
        transport_fee: 25.0,  // Taxa transport implicită
        // Invoice settings
        invoice_prefix: 'TOTH',
        invoice_start_number: 1,
        invoice_current_number: 1,
        // Logo settings
        logo_url: '',
        full_logo_url: ''
    });
    
    // Counters state
    const [counters, setCounters] = useState({
        pallet_counter: 1,
        expense_counter: 1,
        completed_order_counter: 1,
        invoice_counter: 1
    });
    const [savingCounters, setSavingCounters] = useState(false);
    
    // Initial values state (starting balances)
    const [initialValues, setInitialValues] = useState({
        initial_sales: 0,
        initial_expenses: 0
    });
    const [savingInitialValues, setSavingInitialValues] = useState(false);
    const [initialValuesHistory, setInitialValuesHistory] = useState([]);
    
    // New category input
    const [newCategory, setNewCategory] = useState('');
    
    // Edit category state
    const [editingCategory, setEditingCategory] = useState(null);
    const [editedCategoryName, setEditedCategoryName] = useState('');
    
    // Scrap reasons state
    const [scrapReasons, setScrapReasons] = useState([]);
    const [newScrapReason, setNewScrapReason] = useState('');
    
    // Duplicate names cleanup state
    const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
    
    // Stock sync from Excel state
    const [syncingStock, setSyncingStock] = useState(false);
    const stockFileInputRef = useRef(null);
    
    // Fix invoice dates state
    const [fixingInvoiceDates, setFixingInvoiceDates] = useState(false);

    // Reservation boxes state
    const [reservationBoxes, setReservationBoxes] = useState([]);
    const [newBoxNumber, setNewBoxNumber] = useState('');
    const [newBoxCount, setNewBoxCount] = useState('');
    
    // SAGA Sync state
    const [sagaSyncLoading, setSagaSyncLoading] = useState(false);
    const [sagaDifferences, setSagaDifferences] = useState([]);
    const [sagaSelected, setSagaSelected] = useState({});
    const [sagaStats, setSagaStats] = useState(null);
    const [showSagaConfirm, setShowSagaConfirm] = useState(false);
    const [applyingSaga, setApplyingSaga] = useState(false);
    const sagaFileInputRef = useRef(null);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            // Load settings
            const settingsRes = await api.get('/settings/v2');
            if (settingsRes.data) {
                setSettings(prev => ({ ...prev, ...settingsRes.data }));
            }
            
            // Load categories
            const catRes = await api.get('/categories');
            setSettings(prev => ({ ...prev, categories: catRes.data || [] }));
            
            // Load counters
            const countersRes = await api.get('/counters');
            if (countersRes.data) {
                setCounters(countersRes.data);
            }
            
            // Load reservation boxes
            const boxesRes = await api.get('/reservation-boxes/v2');
            setReservationBoxes(boxesRes.data || []);
            
            // Load initial values
            const initialRes = await api.get('/initial-values');
            if (initialRes.data) {
                setInitialValues(initialRes.data);
            }
            
            // Load initial values history
            try {
                const historyRes = await api.get('/initial-values/history');
                if (historyRes.data) {
                    setInitialValuesHistory(historyRes.data);
                }
            } catch (e) {
                // History may not exist yet - this is expected
                console.debug('Initial values history not found:', e.message);
            }
            
            // Load scrap reasons
            try {
                const scrapRes = await api.get('/scrap/v2/reasons');
                if (scrapRes.data) {
                    setScrapReasons(scrapRes.data);
                }
            } catch (e) {
                // May not exist yet
                setScrapReasons(['Defect', 'Deteriorat', 'Expirat']);
            }
        } catch (err) {
            console.error('Error loading settings:', err);
        }
        setLoading(false);
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.post('/settings/v2', {
                allow_new_categories: settings.allow_new_categories,
                default_category: settings.default_category,
                comenzi_palet_name: settings.comenzi_palet_name,
                show_comenzi_palet: settings.show_comenzi_palet,
                show_version_switch: settings.show_version_switch,
                allow_delete_pallets: settings.allow_delete_pallets,
                euro_rate: parseFloat(settings.euro_rate) || 5.0,
                transport_fee: parseFloat(settings.transport_fee) || 25.0,
                invoice_prefix: settings.invoice_prefix || 'TOTH',
                invoice_start_number: parseInt(settings.invoice_start_number) || 1,
                logo_url: settings.logo_url || '',
                full_logo_url: settings.full_logo_url || ''
            });
            toast.success('Setări salvate!');
            // Dispatch event so Layout can reload settings
            window.dispatchEvent(new CustomEvent('settings-updated'));
        } catch (err) {
            toast.error('Eroare la salvare');
            console.error(err);
        }
        setSaving(false);
    };
    
    const cleanDuplicateNames = async () => {
        if (!window.confirm('Aceasta va adăuga sufix (_1, _2, etc.) la produsele cu aceeași denumire dar coduri diferite. Continui?')) {
            return;
        }
        setCleaningDuplicates(true);
        try {
            const res = await api.post('/products/v2/duplicates/clean-names');
            toast.success(res.data.message || `Redenumite ${res.data.renamed_count} produse`);
            if (res.data.renamed_count > 0 && res.data.details) {
                console.log('Produse redenumite:', res.data.details);
            }
        } catch (err) {
            toast.error('Eroare la curățare');
            console.error(err);
        }
        setCleaningDuplicates(false);
    };

    // Fix invoice dates for old orders
    const fixInvoiceDates = async () => {
        if (!window.confirm('Aceasta va seta data facturii pentru comenzile vechi bazat pe data completării. Continui?')) {
            return;
        }
        setFixingInvoiceDates(true);
        try {
            const res = await api.post('/orders/v2/fix-invoice-dates');
            toast.success(res.data.message || `Reparat ${res.data.fixed} facturi`);
        } catch (err) {
            toast.error('Eroare la reparare date facturi');
            console.error(err);
        }
        setFixingInvoiceDates(false);
    };

    // Sync stock from Excel file
    const handleStockFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Te rog încarcă un fișier Excel (.xlsx sau .xls)');
            return;
        }
        
        setSyncingStock(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const res = await api.post('/store/v2/sync-from-excel', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(res.data.message || `Stoc sincronizat: ${res.data.updated_count} produse actualizate`);
            if (res.data.details) {
                console.log('Detalii sincronizare:', res.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la sincronizare stoc');
            console.error(err);
        }
        
        setSyncingStock(false);
        // Reset input
        if (stockFileInputRef.current) {
            stockFileInputRef.current.value = '';
        }
    };
    
    // Create a single reservation box
    const createSingleBox = async () => {
        if (!newBoxNumber) {
            toast.error('Introdu un număr sau cod de cutie (ex: 15, M1, L2)');
            return;
        }
        
        // Check if it's M/L box or numeric
        const boxValue = newBoxNumber.toUpperCase();
        const isAlphanumeric = /^[ML]\d+$/.test(boxValue);
        const isNumeric = /^\d+$/.test(newBoxNumber);
        
        if (!isAlphanumeric && !isNumeric) {
            toast.error('Format invalid. Folosește: număr (1-999), M1-M99 sau L1-L99');
            return;
        }
        
        try {
            const boxData = isAlphanumeric 
                ? { box_number: boxValue }
                : { box_number: parseInt(newBoxNumber) };
            await api.post('/reservation-boxes/v2/create', boxData);
            toast.success(`Cutia ${isAlphanumeric ? boxValue : newBoxNumber} creată`);
            setNewBoxNumber('');
            const boxesRes = await api.get('/reservation-boxes/v2');
            setReservationBoxes(boxesRes.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la creare cutie');
        }
    };

    // Create multiple reservation boxes
    const createMultipleBoxes = async () => {
        if (!newBoxCount || parseInt(newBoxCount) < 1) {
            toast.error('Introdu un număr valid');
            return;
        }
        try {
            const res = await api.post('/reservation-boxes/v2/create-multiple', { count: parseInt(newBoxCount) });
            toast.success(res.data.message);
            setNewBoxCount('');
            const boxesRes = await api.get('/reservation-boxes/v2');
            setReservationBoxes(boxesRes.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la creare cutii');
        }
    };

    // Delete a reservation box
    const deleteBox = async (boxNumber) => {
        if (!window.confirm(`Ștergi cutia ${boxNumber}?`)) return;
        try {
            await api.delete(`/reservation-boxes/v2/${boxNumber}`);
            toast.success(`Cutia ${boxNumber} ștearsă`);
            const boxesRes = await api.get('/reservation-boxes/v2');
            setReservationBoxes(boxesRes.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la ștergere');
        }
    };

    // Release a box manually
    const releaseBox = async (customerId) => {
        try {
            const res = await api.post(`/reservation-boxes/v2/release/${customerId}`);
            toast.success(res.data.message);
            const boxesRes = await api.get('/reservation-boxes/v2');
            setReservationBoxes(boxesRes.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la eliberare');
        }
    };
    
    const saveCounters = async () => {
        setSavingCounters(true);
        try {
            const payload = {
                pallet_counter: parseInt(counters.pallet_counter) || 1,
                expense_counter: parseInt(counters.expense_counter) || 1,
                completed_order_counter: parseInt(counters.completed_order_counter) || 1,
                invoice_counter: parseInt(counters.invoice_counter) || 1
            };
            console.log('Saving counters:', payload);
            await api.post('/counters', payload);
            toast.success('Contoare salvate!');
        } catch (err) {
            console.error('Counters error:', err);
            console.error('Response:', err.response?.data);
            toast.error(`Eroare la salvare contoare: ${err.response?.status || err.message}`);
        }
        setSavingCounters(false);
    };
    
    const saveInitialValues = async () => {
        setSavingInitialValues(true);
        try {
            await api.post('/initial-values', {
                initial_sales: parseFloat(initialValues.initial_sales) || 0,
                initial_expenses: parseFloat(initialValues.initial_expenses) || 0
            });
            toast.success('Valori inițiale salvate!');
        } catch (err) {
            toast.error('Eroare la salvare valori inițiale');
            console.error(err);
        }
        setSavingInitialValues(false);
    };
    
    const handleLogoUpload = async (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        
        try {
            const res = await api.post('/settings/v2/upload-logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data?.url) {
                setSettings(prev => ({
                    ...prev,
                    [type === 'logo' ? 'logo_url' : 'full_logo_url']: res.data.url
                }));
                toast.success('Logo încărcat!');
            }
        } catch (err) {
            toast.error('Eroare la încărcare logo');
            console.error(err);
        }
    };

    const addCategory = async () => {
        if (!newCategory.trim()) return;
        
        try {
            await api.post('/categories', { name: newCategory.trim() });
            setSettings(prev => ({
                ...prev,
                categories: [...prev.categories, newCategory.trim()]
            }));
            setNewCategory('');
            toast.success('Categorie adăugată!');
        } catch (err) {
            toast.error('Eroare la adăugare');
        }
    };

    const deleteCategory = async (category) => {
        if (!window.confirm(`Sigur vrei să ștergi categoria "${category}"?`)) return;
        
        try {
            await api.delete(`/categories/${encodeURIComponent(category)}`);
            setSettings(prev => ({
                ...prev,
                categories: prev.categories.filter(c => c !== category)
            }));
            toast.success('Categorie ștearsă!');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la ștergere');
        }
    };

    const updateCategory = async (oldName, newName) => {
        if (!newName.trim() || newName.trim() === oldName) {
            setEditingCategory(null);
            return;
        }
        try {
            await api.put(`/categories/${encodeURIComponent(oldName)}`, { new_name: newName.trim() });
            setSettings(prev => ({
                ...prev,
                categories: prev.categories.map(c => c === oldName ? newName.trim() : c),
                default_category: prev.default_category === oldName ? newName.trim() : prev.default_category
            }));
            setEditingCategory(null);
            toast.success('Categorie redenumită! Toate produsele au fost actualizate.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la redenumire');
        }
    };

    // Scrap reasons functions
    const addScrapReason = async () => {
        if (!newScrapReason.trim()) return;
        const reason = newScrapReason.trim();
        if (scrapReasons.includes(reason)) {
            toast.error('Motivul există deja');
            return;
        }
        
        try {
            await api.post('/scrap/v2/reasons', { reason });
            setScrapReasons(prev => [...prev, reason]);
            setNewScrapReason('');
            toast.success('Motiv SCRAP adăugat!');
        } catch (err) {
            // If endpoint doesn't exist yet, just update locally
            setScrapReasons(prev => [...prev, reason]);
            setNewScrapReason('');
            toast.success('Motiv SCRAP adăugat!');
        }
    };

    const deleteScrapReason = async (reason) => {
        try {
            await api.delete(`/scrap/v2/reasons/${encodeURIComponent(reason)}`);
            setScrapReasons(prev => prev.filter(r => r !== reason));
            toast.success('Motiv șters!');
        } catch (err) {
            // If endpoint doesn't exist yet, just update locally
            setScrapReasons(prev => prev.filter(r => r !== reason));
            toast.success('Motiv șters!');
        }
    };

    // Backup state and functions
    const [backupSettings, setBackupSettings] = useState({ auto_backup: 'disabled', last_backup: '' });
    const [backups, setBackups] = useState([]);
    const [backupLoading, setBackupLoading] = useState(false);
    const [restoringId, setRestoringId] = useState(null);

    useEffect(() => {
        loadBackupSettings();
        loadBackups();
    }, []);

    const loadBackupSettings = async () => {
        try {
            const res = await api.get('/backup/settings');
            setBackupSettings(res.data);
        } catch (err) {
            console.error('Error loading backup settings:', err);
        }
    };

    const loadBackups = async () => {
        try {
            const res = await api.get('/backup/list');
            setBackups(res.data || []);
        } catch (err) {
            console.error('Error loading backups:', err);
        }
    };

    const saveBackupSettings = async (newAutoBackup) => {
        try {
            await api.post('/backup/settings', { auto_backup: newAutoBackup });
            setBackupSettings(prev => ({ ...prev, auto_backup: newAutoBackup }));
            toast.success('Setări backup salvate!');
        } catch (err) {
            toast.error('Eroare la salvare setări backup');
        }
    };

    const createBackup = async () => {
        setBackupLoading(true);
        try {
            const res = await api.post('/backup/create');
            toast.success(`Backup creat! ${res.data.collections?.length || 0} colecții salvate.`);
            loadBackups();
            loadBackupSettings();
        } catch (err) {
            toast.error('Eroare la creare backup');
        }
        setBackupLoading(false);
    };

    const restoreBackup = async (backupId) => {
        if (!window.confirm('Ești sigur? Toate datele curente vor fi înlocuite cu cele din backup!')) return;
        
        setRestoringId(backupId);
        try {
            const res = await api.post(`/backup/restore/${backupId}`);
            toast.success(`Backup restaurat! ${res.data.restored?.length || 0} colecții restaurate.`);
        } catch (err) {
            toast.error('Eroare la restaurare backup');
        }
        setRestoringId(null);
    };

    const deleteBackup = async (backupId) => {
        if (!window.confirm('Ștergi acest backup?')) return;
        
        try {
            await api.delete(`/backup/${backupId}`);
            toast.success('Backup șters!');
            loadBackups();
        } catch (err) {
            toast.error('Eroare la ștergere backup');
        }
    };
    
    // SAGA Sync Functions
    const handleSagaFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setSagaSyncLoading(true);
        setSagaDifferences([]);
        setSagaSelected({});
        setSagaStats(null);
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const res = await api.post('/saga/compare', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setSagaStats({
                sagaCount: res.data.saga_count,
                ourCount: res.data.our_count,
                matchedCount: res.data.matched_count
            });
            setSagaDifferences(res.data.differences || []);
            
            // Pre-select all differences
            const selected = {};
            (res.data.differences || []).forEach(d => {
                selected[d.barcode] = true;
            });
            setSagaSelected(selected);
            
            if (res.data.differences?.length > 0) {
                toast.success(`Găsite ${res.data.differences.length} diferențe de sufix`);
            } else {
                toast.info('Nu s-au găsit diferențe de sufix între SAGA și baza noastră');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la încărcarea fișierului');
        } finally {
            setSagaSyncLoading(false);
            if (sagaFileInputRef.current) sagaFileInputRef.current.value = '';
        }
    };
    
    const handleToggleSagaItem = (barcode) => {
        setSagaSelected(prev => ({
            ...prev,
            [barcode]: !prev[barcode]
        }));
    };
    
    const handleSelectAllSaga = () => {
        const allSelected = Object.values(sagaSelected).every(v => v);
        const newSelected = {};
        sagaDifferences.forEach(d => {
            newSelected[d.barcode] = !allSelected;
        });
        setSagaSelected(newSelected);
    };
    
    const handleApplySagaChanges = async () => {
        const selectedUpdates = sagaDifferences.filter(d => sagaSelected[d.barcode]);
        if (selectedUpdates.length === 0) {
            toast.error('Selectează cel puțin un produs pentru actualizare');
            return;
        }
        
        setApplyingSaga(true);
        try {
            const res = await api.post('/saga/apply', {
                updates: selectedUpdates.map(d => ({
                    barcode: d.barcode,
                    our_name: d.our_name,
                    saga_name: d.saga_name,
                    suffix: d.suffix
                }))
            });
            
            toast.success(`Actualizate ${res.data.updated_count} produse cu succes`);
            if (res.data.errors?.length > 0) {
                toast.warning(`${res.data.errors.length} erori la actualizare`);
            }
            
            // Clear state after successful apply
            setSagaDifferences([]);
            setSagaSelected({});
            setSagaStats(null);
            setShowSagaConfirm(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la aplicarea modificărilor');
        } finally {
            setApplyingSaga(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="pb-24 max-w-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Setări
                    </h1>
                    <p className="text-zinc-500 mt-1">Configurare aplicație V2</p>
                </div>
                <Button 
                    onClick={saveSettings} 
                    disabled={saving}
                    className="bg-emerald-500 hover:bg-emerald-600"
                >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Se salvează...' : 'Salvează'}
                </Button>
            </div>

            {/* Euro Rate Setting */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Euro size={20} className="text-blue-500" />
                    Curs Valutar
                </h2>
                
                <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <Label>Curs EUR (în RON)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={settings.euro_rate}
                            onChange={(e) => setSettings(prev => ({ ...prev, euro_rate: e.target.value }))}
                            className="mt-1"
                            placeholder="5.00"
                        />
                    </div>
                    <div className="pt-6 text-sm text-zinc-500">
                        1 EUR = <span className="font-bold text-blue-600">{parseFloat(settings.euro_rate || 5).toFixed(2)}</span> RON
                    </div>
                </div>
            </div>

            {/* Transport Fee Setting */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Package size={20} className="text-orange-500" />
                    Taxa Transport
                </h2>
                
                <div className="flex items-center gap-4">
                    <div className="flex-1">
                        <Label>Valoare taxă transport (RON)</Label>
                        <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={settings.transport_fee ?? 25}
                            onChange={(e) => setSettings(prev => ({ ...prev, transport_fee: e.target.value }))}
                            className="mt-1"
                            placeholder="25.00"
                        />
                    </div>
                    <div className="pt-6 text-sm text-zinc-500">
                        Taxa transport: <span className="font-bold text-orange-600">{parseFloat(settings.transport_fee || 25).toFixed(2)}</span> RON / comandă
                    </div>
                </div>
                <p className="mt-2 text-xs text-zinc-400">
                    Această taxă se adaugă automat la fiecare comandă nouă
                </p>
            </div>

            {/* Invoice Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-violet-500" />
                    Setări Facturi
                </h2>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <Label>Prefix Factură</Label>
                        <Input
                            value={settings.invoice_prefix}
                            onChange={(e) => setSettings(prev => ({ ...prev, invoice_prefix: e.target.value }))}
                            className="mt-1"
                            placeholder="TOTH"
                        />
                    </div>
                    <div>
                        <Label>Număr de Start</Label>
                        <Input
                            type="number"
                            min="1"
                            value={settings.invoice_start_number}
                            onChange={(e) => setSettings(prev => ({ ...prev, invoice_start_number: e.target.value }))}
                            className="mt-1"
                            placeholder="1"
                        />
                    </div>
                </div>
                
                <div className="p-3 bg-zinc-50 rounded-lg">
                    <p className="text-sm text-zinc-600">
                        Exemplu număr factură: <span className="font-bold text-violet-600">
                            {settings.invoice_prefix || 'TOTH'}{String(settings.invoice_start_number || 1).padStart(4, '0')}
                        </span>
                    </p>
                    {settings.invoice_current_number > 1 && (
                        <p className="text-xs text-zinc-400 mt-1">
                            Ultima factură emisă: {settings.invoice_prefix || 'TOTH'}{String(settings.invoice_current_number - 1).padStart(4, '0')}
                        </p>
                    )}
                </div>
            </div>

            {/* Logo Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Image size={20} className="text-emerald-500" />
                    Logo-uri
                </h2>
                
                <div className="grid grid-cols-2 gap-6">
                    {/* Logo mic */}
                    <div>
                        <Label className="mb-2 block">Logo (mic)</Label>
                        <p className="text-xs text-zinc-400 mb-2">Folosit în bara de navigare mobilă</p>
                        <div className="border-2 border-dashed border-zinc-200 rounded-lg p-4 text-center">
                            {settings.logo_url ? (
                                <div className="space-y-2">
                                    <img 
                                        src={settings.logo_url} 
                                        alt="Logo" 
                                        className="max-h-20 mx-auto object-contain"
                                    />
                                    <div className="flex gap-2 justify-center">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => logoInputRef.current?.click()}
                                        >
                                            Schimbă
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                            onClick={() => setSettings(prev => ({ ...prev, logo_url: '' }))}
                                        >
                                            Șterge
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    variant="outline"
                                    onClick={() => logoInputRef.current?.click()}
                                >
                                    <Upload size={16} className="mr-2" />
                                    Încarcă Logo
                                </Button>
                            )}
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleLogoUpload(e, 'logo')}
                            />
                        </div>
                    </div>
                    
                    {/* Full Logo */}
                    <div>
                        <Label className="mb-2 block">Logo Complet</Label>
                        <p className="text-xs text-zinc-400 mb-2">Folosit în sidebar și facturi</p>
                        <div className="border-2 border-dashed border-zinc-200 rounded-lg p-4 text-center">
                            {settings.full_logo_url ? (
                                <div className="space-y-2">
                                    <img 
                                        src={settings.full_logo_url} 
                                        alt="Full Logo" 
                                        className="max-h-20 mx-auto object-contain"
                                    />
                                    <div className="flex gap-2 justify-center">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => fullLogoInputRef.current?.click()}
                                        >
                                            Schimbă
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                            onClick={() => setSettings(prev => ({ ...prev, full_logo_url: '' }))}
                                        >
                                            Șterge
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    variant="outline"
                                    onClick={() => fullLogoInputRef.current?.click()}
                                >
                                    <Upload size={16} className="mr-2" />
                                    Încarcă Logo Complet
                                </Button>
                            )}
                            <input
                                ref={fullLogoInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleLogoUpload(e, 'full_logo')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Categories Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Tag size={20} className="text-violet-500" />
                    Categorii Produse
                </h2>
                
                {/* Toggle for new categories */}
                <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg mb-4">
                    <div>
                        <p className="font-medium text-zinc-900">Permite categorii noi</p>
                        <p className="text-sm text-zinc-500">
                            {settings.allow_new_categories 
                                ? 'Poți crea categorii noi la recepție' 
                                : 'Doar categoriile existente pot fi selectate'}
                        </p>
                    </div>
                    <Switch
                        checked={settings.allow_new_categories}
                        onCheckedChange={(checked) => 
                            setSettings(prev => ({ ...prev, allow_new_categories: checked }))
                        }
                    />
                </div>

                {/* Default category */}
                <div className="mb-4">
                    <Label>Categorie implicită</Label>
                    <select
                        value={settings.default_category}
                        onChange={(e) => setSettings(prev => ({ ...prev, default_category: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                        <option value="">Fără categorie implicită</option>
                        {settings.categories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                {/* Add new category */}
                <div className="flex gap-2 mb-4">
                    <Input
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="Categorie nouă..."
                        onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                    />
                    <Button onClick={addCategory} className="bg-violet-500 hover:bg-violet-600">
                        <Plus size={18} />
                    </Button>
                </div>

                {/* Categories list */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {settings.categories.length === 0 ? (
                        <p className="text-zinc-400 text-center py-4">Nu există categorii</p>
                    ) : (
                        settings.categories.map((category) => (
                            <div
                                key={category}
                                className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                            >
                                {editingCategory === category ? (
                                    // Edit mode
                                    <div className="flex items-center gap-2 flex-1">
                                        <Tag size={16} className="text-zinc-400" />
                                        <Input
                                            value={editedCategoryName}
                                            onChange={(e) => setEditedCategoryName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    updateCategory(category, editedCategoryName);
                                                } else if (e.key === 'Escape') {
                                                    setEditingCategory(null);
                                                }
                                            }}
                                            className="h-8 flex-1"
                                            autoFocus
                                        />
                                        <button
                                            onClick={() => updateCategory(category, editedCategoryName)}
                                            className="p-1 text-emerald-500 hover:bg-emerald-50 rounded"
                                        >
                                            <Check size={16} />
                                        </button>
                                        <button
                                            onClick={() => setEditingCategory(null)}
                                            className="p-1 text-zinc-500 hover:bg-zinc-100 rounded"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    // View mode
                                    <>
                                        <div className="flex items-center gap-2">
                                            <Tag size={16} className="text-zinc-400" />
                                            <span className="font-medium">{category}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {settings.default_category === category && (
                                                <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded">
                                                    Implicit
                                                </span>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setEditingCategory(category);
                                                    setEditedCategoryName(category);
                                                }}
                                                className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                                                title="Editează categoria"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                onClick={() => deleteCategory(category)}
                                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                title="Șterge categoria"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Scrap Reasons Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Trash2 size={20} className="text-red-500" />
                    Motive SCRAP
                </h2>
                
                <p className="text-sm text-zinc-500 mb-4">
                    Lista de motive disponibile când muți produse la SCRAP.
                </p>

                {/* Add new scrap reason */}
                <div className="flex gap-2 mb-4">
                    <Input
                        value={newScrapReason}
                        onChange={(e) => setNewScrapReason(e.target.value)}
                        placeholder="Adaugă motiv nou (ex: Defect, Deteriorat)..."
                        onKeyDown={(e) => e.key === 'Enter' && addScrapReason()}
                    />
                    <Button onClick={addScrapReason} className="bg-red-500 hover:bg-red-600">
                        <Plus size={18} />
                    </Button>
                </div>

                {/* Scrap reasons list */}
                <div className="flex flex-wrap gap-2">
                    {scrapReasons.length === 0 ? (
                        <p className="text-zinc-400 text-center py-4 w-full">Nu există motive definite</p>
                    ) : (
                        scrapReasons.map((reason) => (
                            <div
                                key={reason}
                                className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg"
                            >
                                <span>{reason}</span>
                                <button
                                    onClick={() => deleteScrapReason(reason)}
                                    className="p-0.5 hover:bg-red-100 rounded"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Visibility Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Eye size={20} className="text-violet-500" />
                    Vizibilitate Meniu
                </h2>

                {/* Show/Hide Version Switch */}
                <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                    <div>
                        <p className="font-medium text-zinc-900">Switch Versiune (V1/V2)</p>
                        <p className="text-sm text-zinc-500">
                            {settings.show_version_switch 
                                ? 'Selector versiune vizibil în meniu' 
                                : 'Selector versiune ascuns din meniu'}
                        </p>
                    </div>
                    <Switch
                        checked={settings.show_version_switch}
                        onCheckedChange={(checked) => 
                            setSettings(prev => ({ ...prev, show_version_switch: checked }))
                        }
                    />
                </div>
                
                {/* Allow Delete Pallets */}
                <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                    <div>
                        <p className="font-medium text-zinc-900">Ștergere Paleți din Istoric</p>
                        <p className="text-sm text-zinc-500">
                            {settings.allow_delete_pallets 
                                ? 'Butonul de ștergere este vizibil în Istoric Paleți' 
                                : 'Butonul de ștergere este ascuns (protecție date)'}
                        </p>
                    </div>
                    <Switch
                        checked={settings.allow_delete_pallets}
                        onCheckedChange={(checked) => 
                            setSettings(prev => ({ ...prev, allow_delete_pallets: checked }))
                        }
                    />
                </div>
            </div>

            {/* Initial Values Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                        <TrendingUp size={20} className="text-emerald-500" />
                        Schimbare An (Import Date Anuale)
                    </h2>
                    <Button 
                        onClick={saveInitialValues} 
                        disabled={savingInitialValues}
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600"
                    >
                        <Save size={16} className="mr-2" />
                        {savingInitialValues ? 'Se salvează...' : 'Salvează Valori'}
                    </Button>
                </div>
                
                <p className="text-sm text-zinc-500 mb-4">
                    Introduceți valorile cu care începeți anul (sold anterior). Acestea se vor adăuga la calculele din rapoarte anuale.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                    {/* Initial Sales */}
                    <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                        <Label className="text-sm font-medium text-emerald-700">Vânzări An Anterior</Label>
                        <p className="text-xs text-emerald-600 mb-2">Sold vânzări cu care pornești anul</p>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={initialValues.initial_sales}
                                onChange={(e) => setInitialValues(prev => ({ ...prev, initial_sales: e.target.value }))}
                                className="mt-1"
                                placeholder="0.00"
                            />
                            <span className="text-emerald-600 font-medium">RON</span>
                        </div>
                    </div>
                    
                    {/* Initial Expenses */}
                    <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                        <Label className="text-sm font-medium text-red-700">Cheltuieli An Anterior</Label>
                        <p className="text-xs text-red-600 mb-2">Sold cheltuieli cu care pornești anul</p>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={initialValues.initial_expenses}
                                onChange={(e) => setInitialValues(prev => ({ ...prev, initial_expenses: e.target.value }))}
                                className="mt-1"
                                placeholder="0.00"
                            />
                            <span className="text-red-600 font-medium">RON</span>
                        </div>
                    </div>
                </div>
                
                {/* History section */}
                {initialValuesHistory.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-zinc-200">
                        <h3 className="text-sm font-medium text-zinc-700 mb-2">Istoric Valori Importate</h3>
                        <div className="space-y-2">
                            {initialValuesHistory.map((h, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-zinc-50 rounded text-sm">
                                    <span className="font-medium text-zinc-600">An {h.year}</span>
                                    <div className="flex items-center gap-4">
                                        <span className="text-emerald-600">Vânzări: {h.initial_sales?.toFixed(2)} RON</span>
                                        <span className="text-red-600">Cheltuieli: {h.initial_expenses?.toFixed(2)} RON</span>
                                    </div>
                                    <span className="text-xs text-zinc-400">
                                        {h.updated_at ? new Date(h.updated_at).toLocaleDateString('ro-RO') : ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Counters Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                        <Hash size={20} className="text-amber-500" />
                        Numerotare Automată
                    </h2>
                    <Button 
                        onClick={saveCounters} 
                        disabled={savingCounters}
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600"
                    >
                        <Save size={16} className="mr-2" />
                        {savingCounters ? 'Se salvează...' : 'Salvează Contoare'}
                    </Button>
                </div>
                
                <p className="text-sm text-zinc-500 mb-4">
                    Setează valorile de start pentru numerotarea automată. Sistemul va continua progresiv de la aceste valori.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                    {/* Pallet Counter */}
                    <div className="p-4 bg-zinc-50 rounded-lg">
                        <Label className="text-sm font-medium text-zinc-700">Numerotare Paleți</Label>
                        <p className="text-xs text-zinc-400 mb-2">Următorul palet va avea acest număr</p>
                        <Input
                            type="number"
                            min="1"
                            value={counters.pallet_counter}
                            onChange={(e) => setCounters(prev => ({ ...prev, pallet_counter: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                    
                    {/* Invoice Counter */}
                    <div className="p-4 bg-zinc-50 rounded-lg">
                        <Label className="text-sm font-medium text-zinc-700">Numerotare Facturi</Label>
                        <p className="text-xs text-zinc-400 mb-2">Următoarea factură: {settings.invoice_prefix || 'TOTH'}{String(counters.invoice_counter || 1).padStart(4, '0')}</p>
                        <Input
                            type="number"
                            min="1"
                            value={counters.invoice_counter}
                            onChange={(e) => setCounters(prev => ({ ...prev, invoice_counter: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                    
                    {/* Expense Counter */}
                    <div className="p-4 bg-zinc-50 rounded-lg">
                        <Label className="text-sm font-medium text-zinc-700">Numerotare Cheltuieli</Label>
                        <p className="text-xs text-zinc-400 mb-2">Următoarea cheltuială va avea acest ID</p>
                        <Input
                            type="number"
                            min="1"
                            value={counters.expense_counter}
                            onChange={(e) => setCounters(prev => ({ ...prev, expense_counter: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                    
                    {/* Completed Order Counter */}
                    <div className="p-4 bg-zinc-50 rounded-lg">
                        <Label className="text-sm font-medium text-zinc-700">Numerotare Comenzi Finalizate</Label>
                        <p className="text-xs text-zinc-400 mb-2">Următoarea comandă finalizată va avea acest nr.</p>
                        <Input
                            type="number"
                            min="1"
                            value={counters.completed_order_counter}
                            onChange={(e) => setCounters(prev => ({ ...prev, completed_order_counter: e.target.value }))}
                            className="mt-1"
                        />
                    </div>
                </div>
            </div>

            {/* Backup Settings */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Package size={20} className="text-emerald-500" />
                    Backup Date
                </h2>
                
                {/* Auto backup settings */}
                <div className="mb-4">
                    <Label className="text-sm font-medium mb-2 block">Backup Automat</Label>
                    <div className="flex gap-2">
                        {['disabled', 'daily', 'weekly', 'monthly'].map(option => (
                            <button
                                key={option}
                                onClick={() => saveBackupSettings(option)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                    backupSettings.auto_backup === option
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                                }`}
                            >
                                {option === 'disabled' ? 'Dezactivat' :
                                 option === 'daily' ? 'Zilnic' :
                                 option === 'weekly' ? 'Săptămânal' : 'Lunar'}
                            </button>
                        ))}
                    </div>
                    {backupSettings.last_backup && (
                        <p className="text-xs text-zinc-500 mt-2">
                            Ultimul backup: {new Date(backupSettings.last_backup).toLocaleString('ro-RO')}
                        </p>
                    )}
                </div>
                
                {/* Manual backup button */}
                <Button
                    onClick={createBackup}
                    disabled={backupLoading}
                    className="w-full mb-4 bg-emerald-500 hover:bg-emerald-600"
                >
                    {backupLoading ? (
                        <>
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Se creează backup...
                        </>
                    ) : (
                        <>
                            <Save size={16} className="mr-2" />
                            Backup Acum
                        </>
                    )}
                </Button>
                
                {/* Backup list */}
                <div className="border-t border-zinc-200 pt-4">
                    <Label className="text-sm font-medium mb-2 block">Backup-uri Disponibile ({backups.length})</Label>
                    {backups.length === 0 ? (
                        <p className="text-sm text-zinc-500 text-center py-4">Nu există backup-uri</p>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {backups.map(backup => (
                                <div 
                                    key={backup.id}
                                    className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                                >
                                    <div>
                                        <p className="font-medium text-sm">{backup.date}</p>
                                        <p className="text-xs text-zinc-500">
                                            {backup.collections?.length || 0} colecții • 
                                            {new Date(backup.created_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => restoreBackup(backup.id)}
                                            disabled={restoringId === backup.id}
                                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                        >
                                            {restoringId === backup.id ? (
                                                <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                            ) : (
                                                'Restaurează'
                                            )}
                                        </Button>
                                        <button
                                            onClick={() => deleteBackup(backup.id)}
                                            className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Maintenance / Sync Section */}
            <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <Package size={20} className="text-teal-500" />
                    Întreținere & Sincronizare
                </h2>
                
                <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                        <div>
                            <p className="font-medium text-zinc-800">Sincronizare produse depozit</p>
                            <p className="text-xs text-zinc-500 mt-1">
                                Adaugă produsele din depozit în catalogul de produse pentru căutare
                            </p>
                        </div>
                        <Button
                            onClick={async () => {
                                try {
                                    const res = await api.post('/depot/v2/sync-products-to-catalog');
                                    toast.success(`Sincronizare completă: ${res.data.synced} produse adăugate`);
                                } catch (err) {
                                    toast.error('Eroare la sincronizare');
                                }
                            }}
                            variant="outline"
                            size="sm"
                            className="text-teal-600 border-teal-200 hover:bg-teal-50"
                        >
                            Sincronizează
                        </Button>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div>
                            <p className="font-medium text-amber-800">Curăță denumiri duplicate</p>
                            <p className="text-xs text-amber-600 mt-1">
                                Adaugă sufix (_1, _2) la produsele cu aceeași denumire dar coduri diferite
                            </p>
                        </div>
                        <Button
                            onClick={cleanDuplicateNames}
                            disabled={cleaningDuplicates}
                            variant="outline"
                            size="sm"
                            className="text-amber-600 border-amber-300 hover:bg-amber-100"
                        >
                            {cleaningDuplicates ? 'Se curăță...' : 'Curăță'}
                        </Button>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div>
                            <p className="font-medium text-blue-800">Sincronizare Stoc din Excel</p>
                            <p className="text-xs text-blue-600 mt-1">
                                Actualizează cantitățile din magazin dintr-un fișier Excel (coloane: Cod/Barcode, Cantitate)
                            </p>
                        </div>
                        <div>
                            <input
                                ref={stockFileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleStockFileUpload}
                                className="hidden"
                            />
                            <Button
                                onClick={() => stockFileInputRef.current?.click()}
                                disabled={syncingStock}
                                variant="outline"
                                size="sm"
                                className="text-blue-600 border-blue-300 hover:bg-blue-100"
                            >
                                {syncingStock ? 'Se sincronizează...' : 'Încarcă Excel'}
                            </Button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div>
                            <p className="font-medium text-purple-800">Repară Date Facturi</p>
                            <p className="text-xs text-purple-600 mt-1">
                                Setează data facturii pentru comenzile vechi (bazat pe data completării)
                            </p>
                        </div>
                        <Button
                            onClick={fixInvoiceDates}
                            disabled={fixingInvoiceDates}
                            variant="outline"
                            size="sm"
                            className="text-purple-600 border-purple-300 hover:bg-purple-100"
                        >
                            {fixingInvoiceDates ? 'Se repară...' : 'Repară'}
                        </Button>
                    </div>

                    {/* SAGA Sync Section */}
                    <div className="mt-4 p-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg border border-violet-200">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h3 className="font-semibold text-violet-800 flex items-center gap-2">
                                    <FileText size={18} />
                                    Sincronizare SAGA
                                </h3>
                                <p className="text-xs text-violet-600 mt-1">
                                    Compară denumirile produselor cu lista SAGA și adaugă sufixele lipsă (_1, _2, etc.)
                                </p>
                            </div>
                            <div>
                                <input
                                    ref={sagaFileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls,.txt,.csv"
                                    onChange={handleSagaFileUpload}
                                    className="hidden"
                                    data-testid="saga-file-input"
                                />
                                <Button
                                    onClick={() => sagaFileInputRef.current?.click()}
                                    disabled={sagaSyncLoading}
                                    className="bg-violet-500 hover:bg-violet-600 text-white"
                                    data-testid="saga-upload-btn"
                                >
                                    <Upload size={16} className="mr-2" />
                                    {sagaSyncLoading ? 'Se analizează...' : 'Încarcă Lista SAGA'}
                                </Button>
                            </div>
                        </div>
                        
                        {/* Stats after file upload */}
                        {sagaStats && (
                            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                                <div className="bg-white/60 rounded p-2">
                                    <div className="text-lg font-bold text-violet-700">{sagaStats.sagaCount}</div>
                                    <div className="text-xs text-zinc-600">Produse SAGA</div>
                                </div>
                                <div className="bg-white/60 rounded p-2">
                                    <div className="text-lg font-bold text-indigo-700">{sagaStats.matchedCount}</div>
                                    <div className="text-xs text-zinc-600">Potrivite</div>
                                </div>
                                <div className="bg-white/60 rounded p-2">
                                    <div className="text-lg font-bold text-orange-600">{sagaDifferences.length}</div>
                                    <div className="text-xs text-zinc-600">Diferențe sufix</div>
                                </div>
                            </div>
                        )}
                        
                        {/* Differences Table */}
                        {sagaDifferences.length > 0 && (
                            <div className="bg-white rounded-lg border border-violet-200 overflow-hidden">
                                <div className="p-2 bg-violet-100 flex items-center justify-between">
                                    <label className="flex items-center gap-2 text-sm text-violet-800 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={Object.values(sagaSelected).every(v => v)}
                                            onChange={handleSelectAllSaga}
                                            className="rounded border-violet-300"
                                        />
                                        Selectează toate ({Object.values(sagaSelected).filter(v => v).length}/{sagaDifferences.length})
                                    </label>
                                    <Button
                                        onClick={() => setShowSagaConfirm(true)}
                                        disabled={Object.values(sagaSelected).filter(v => v).length === 0}
                                        size="sm"
                                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                        data-testid="saga-confirm-btn"
                                    >
                                        <Check size={14} className="mr-1" />
                                        Confirmă Modificările
                                    </Button>
                                </div>
                                <div className="max-h-[300px] overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-zinc-50 sticky top-0">
                                            <tr>
                                                <th className="p-2 text-left w-10"></th>
                                                <th className="p-2 text-left">Cod</th>
                                                <th className="p-2 text-left">Denumirea Noastră</th>
                                                <th className="p-2 text-left">Denumirea SAGA</th>
                                                <th className="p-2 text-left">Sufix</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sagaDifferences.map((diff, idx) => (
                                                <tr 
                                                    key={diff.barcode} 
                                                    className={`border-t border-zinc-100 hover:bg-violet-50/50 cursor-pointer ${sagaSelected[diff.barcode] ? 'bg-emerald-50/50' : ''}`}
                                                    onClick={() => handleToggleSagaItem(diff.barcode)}
                                                >
                                                    <td className="p-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={sagaSelected[diff.barcode] || false}
                                                            onChange={() => handleToggleSagaItem(diff.barcode)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="rounded border-zinc-300"
                                                        />
                                                    </td>
                                                    <td className="p-2 font-mono text-xs text-zinc-600">{diff.barcode}</td>
                                                    <td className="p-2 text-zinc-800">{diff.our_name}</td>
                                                    <td className="p-2 text-violet-700">{diff.saga_name}</td>
                                                    <td className="p-2">
                                                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                                            {diff.suffix}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        
                        {/* Preview: what names will look like */}
                        {sagaDifferences.length > 0 && Object.values(sagaSelected).some(v => v) && (
                            <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200">
                                <p className="text-xs text-emerald-700 font-medium mb-1">Previzualizare modificări:</p>
                                <div className="text-xs text-emerald-800 max-h-20 overflow-y-auto">
                                    {sagaDifferences.filter(d => sagaSelected[d.barcode]).slice(0, 5).map(d => (
                                        <div key={d.barcode}>
                                            <span className="text-zinc-500">{d.our_name}</span> → <span className="font-medium">{d.new_name}</span>
                                        </div>
                                    ))}
                                    {Object.values(sagaSelected).filter(v => v).length > 5 && (
                                        <div className="text-zinc-500 mt-1">...și încă {Object.values(sagaSelected).filter(v => v).length - 5} modificări</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* SAGA Confirmation Modal */}
                    {showSagaConfirm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSagaConfirm(false)}>
                            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
                                <h3 className="text-lg font-bold text-zinc-800 mb-3">Confirmare Finală</h3>
                                <p className="text-zinc-600 mb-4">
                                    Ești sigur că vrei să actualizezi <strong className="text-violet-700">{Object.values(sagaSelected).filter(v => v).length}</strong> produse?
                                </p>
                                <p className="text-sm text-zinc-500 mb-4 p-2 bg-amber-50 rounded border border-amber-200">
                                    <strong>Atenție:</strong> Această acțiune va adăuga sufixul SAGA la numele produselor selectate în toate colecțiile (magazin, depozit, paleți, rezervări, comenzi).
                                </p>
                                <div className="flex gap-3 justify-end">
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowSagaConfirm(false)}
                                        disabled={applyingSaga}
                                    >
                                        Anulează
                                    </Button>
                                    <Button
                                        onClick={handleApplySagaChanges}
                                        disabled={applyingSaga}
                                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                        data-testid="saga-apply-btn"
                                    >
                                        {applyingSaga ? 'Se aplică...' : 'Confirmă și Aplică'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                        <div>
                            <p className="font-medium text-zinc-800">Recuperare clienți lipsă</p>
                            <p className="text-xs text-zinc-500 mt-1">
                                Găsește clienții cu facturi care nu apar în lista de clienți
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={async () => {
                                    try {
                                        const res = await api.get('/customers/v2/check-missing');
                                        if (res.data.missing_count === 0) {
                                            toast.success('Nu există clienți lipsă!');
                                        } else {
                                            toast.warning(`Găsiți ${res.data.missing_count} clienți lipsă: ${res.data.missing_customers.slice(0, 5).join(', ')}${res.data.missing_count > 5 ? '...' : ''}`);
                                        }
                                    } catch (err) {
                                        toast.error('Eroare la verificare');
                                    }
                                }}
                                variant="outline"
                                size="sm"
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                                Verifică
                            </Button>
                            <Button
                                onClick={async () => {
                                    try {
                                        const res = await api.post('/customers/v2/sync-from-orders');
                                        if (res.data.created_count === 0) {
                                            toast.success('Toți clienții sunt sincronizați!');
                                        } else {
                                            toast.success(`Recuperați ${res.data.created_count} clienți: ${res.data.created_customers.slice(0, 5).join(', ')}`);
                                        }
                                    } catch (err) {
                                        toast.error('Eroare la recuperare');
                                    }
                                }}
                                variant="outline"
                                size="sm"
                                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                            >
                                Recuperează
                            </Button>
                        </div>
                    </div>

                    {/* Reservation Boxes Management */}
                    <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                            <Package size={18} />
                            Cutii pentru Rezervări
                        </h3>
                        
                        {/* Create boxes */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    value={newBoxNumber}
                                    onChange={(e) => setNewBoxNumber(e.target.value)}
                                    placeholder="Nr. cutie"
                                    className="w-24"
                                />
                                <Button
                                    onClick={createSingleBox}
                                    size="sm"
                                    variant="outline"
                                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                >
                                    Creează
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    min="1"
                                    value={newBoxCount}
                                    onChange={(e) => setNewBoxCount(e.target.value)}
                                    placeholder="Câte?"
                                    className="w-20"
                                />
                                <Button
                                    onClick={createMultipleBoxes}
                                    size="sm"
                                    variant="outline"
                                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                >
                                    +Multiple
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    type="text"
                                    value={newBoxNumber}
                                    onChange={(e) => setNewBoxNumber(e.target.value.toUpperCase())}
                                    placeholder="M1, L1..."
                                    className="w-20"
                                />
                                <Button
                                    onClick={createSingleBox}
                                    size="sm"
                                    variant="outline"
                                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                                >
                                    +M/L
                                </Button>
                            </div>
                        </div>

                        {/* Boxes summary and filters */}
                        {reservationBoxes.length > 0 && (
                            <div className="mb-3 p-2 bg-white rounded border border-amber-200">
                                <div className="flex flex-wrap gap-3 text-sm">
                                    <span className="font-semibold">
                                        Total: {reservationBoxes.length} cutii
                                    </span>
                                    <span className="text-green-600">
                                        {reservationBoxes.filter(b => b.status === 'free').length} libere
                                    </span>
                                    <span className="text-amber-600">
                                        {reservationBoxes.filter(b => b.status !== 'free').length} ocupate
                                    </span>
                                    <span className="border-l pl-3 text-zinc-500">
                                        Numerice: {reservationBoxes.filter(b => typeof b.box_number === 'number').length}
                                    </span>
                                    <span className="text-purple-600">
                                        M: {reservationBoxes.filter(b => String(b.box_number).startsWith('M')).length}
                                    </span>
                                    <span className="text-indigo-600">
                                        L: {reservationBoxes.filter(b => String(b.box_number).startsWith('L')).length}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Numeric Boxes */}
                        {reservationBoxes.filter(b => typeof b.box_number === 'number').length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-sm font-semibold text-zinc-700 mb-2">Cutii Numerotate</h4>
                                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5 max-h-48 overflow-y-auto p-2 bg-white rounded border">
                                    {reservationBoxes
                                        .filter(b => typeof b.box_number === 'number')
                                        .sort((a, b) => a.box_number - b.box_number)
                                        .map(box => (
                                        <div
                                            key={box.box_number}
                                            className={`p-1.5 rounded border text-center text-xs ${
                                                box.status === 'free' 
                                                    ? 'bg-white border-zinc-200' 
                                                    : 'bg-amber-100 border-amber-300'
                                            }`}
                                            title={box.customer_id ? `Client: ${box.customer_id}` : 'Liberă'}
                                        >
                                            <div className="font-bold">{box.box_number}</div>
                                            {box.customer_id ? (
                                                <div className="text-amber-700 truncate text-[10px]">
                                                    {box.customer_id}
                                                    <button
                                                        onClick={() => releaseBox(box.customer_id)}
                                                        className="ml-0.5 text-red-500 hover:text-red-700"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => deleteBox(box.box_number)}
                                                    className="text-red-400 hover:text-red-600 text-[10px]"
                                                >
                                                    șterge
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* M Boxes */}
                        {reservationBoxes.filter(b => String(b.box_number).startsWith('M')).length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-sm font-semibold text-purple-700 mb-2">Cutii M (Medium)</h4>
                                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5 p-2 bg-purple-50 rounded border border-purple-200">
                                    {reservationBoxes
                                        .filter(b => String(b.box_number).startsWith('M'))
                                        .sort((a, b) => String(a.box_number).localeCompare(String(b.box_number)))
                                        .map(box => (
                                        <div
                                            key={box.box_number}
                                            className={`p-1.5 rounded border text-center text-xs ${
                                                box.status === 'free' 
                                                    ? 'bg-white border-purple-200' 
                                                    : 'bg-purple-100 border-purple-300'
                                            }`}
                                            title={box.customer_id ? `Client: ${box.customer_id}` : 'Liberă'}
                                        >
                                            <div className="font-bold text-purple-700">{box.box_number}</div>
                                            {box.customer_id ? (
                                                <div className="text-purple-700 truncate text-[10px]">
                                                    {box.customer_id}
                                                    <button
                                                        onClick={() => releaseBox(box.customer_id)}
                                                        className="ml-0.5 text-red-500 hover:text-red-700"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => deleteBox(box.box_number)}
                                                    className="text-red-400 hover:text-red-600 text-[10px]"
                                                >
                                                    șterge
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* L Boxes */}
                        {reservationBoxes.filter(b => String(b.box_number).startsWith('L')).length > 0 && (
                            <div className="mb-4">
                                <h4 className="text-sm font-semibold text-indigo-700 mb-2">Cutii L (Large)</h4>
                                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5 p-2 bg-indigo-50 rounded border border-indigo-200">
                                    {reservationBoxes
                                        .filter(b => String(b.box_number).startsWith('L'))
                                        .sort((a, b) => String(a.box_number).localeCompare(String(b.box_number)))
                                        .map(box => (
                                        <div
                                            key={box.box_number}
                                            className={`p-1.5 rounded border text-center text-xs ${
                                                box.status === 'free' 
                                                    ? 'bg-white border-indigo-200' 
                                                    : 'bg-indigo-100 border-indigo-300'
                                            }`}
                                            title={box.customer_id ? `Client: ${box.customer_id}` : 'Liberă'}
                                        >
                                            <div className="font-bold text-indigo-700">{box.box_number}</div>
                                            {box.customer_id ? (
                                                <div className="text-indigo-700 truncate text-[10px]">
                                                    {box.customer_id}
                                                    <button
                                                        onClick={() => releaseBox(box.customer_id)}
                                                        className="ml-0.5 text-red-500 hover:text-red-700"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => deleteBox(box.box_number)}
                                                    className="text-red-400 hover:text-red-600 text-[10px]"
                                                >
                                                    șterge
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {reservationBoxes.length === 0 && (
                            <p className="text-sm text-amber-600 text-center py-4">
                                Nu există cutii create. Creează cutii pentru a gestiona rezervările fizic.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetariV2;
